// ─── Cluster D-sub — Upload Progress Toast (~110 LoC) ───────────────────────
// Per docs/design/app-js-split-plan-260522.md §3 Module 5.
//
// Exports: uploadToastBegin, uploadToastComplete, uploadToastFail,
//          uploadToastAbortRemaining, uploadToastDismiss, uploadToastCheckDone,
//          uploadToastRender.
//
// Globals (read-only at call time):
//   window.adatool?.log        — logging helper (illegal-transition warning)
//   window._adaUpdateQuotaBadge — Phase 9 bridge: no-op stub in app.js until
//                                 auth-and-billing.mjs is extracted; called once
//                                 per successful upload to reflect balance change.
//                                 Looked up lazily at call time so boot order
//                                 does not matter.
//
// WHY no import from other split modules: the toast SM is a self-contained
// leaf — it holds no cross-module state and calls no sibling functions.
// The only cross-cluster call is _updateQuotaBadge (Cluster H stub) which
// is reached via the window bridge above.

// ─── Upload progress toast state machine ─────────────────────────────────────
//
// Tracks cumulative upload progress across potentially overlapping drops.
// The phase is a validated 5-state machine; total/completed/failed remain
// data fields populated by the begin/complete/fail/abort mutators.
//
// Pattern mirrors LoadingOverlayController in editor/loading-overlay-state.js
// and AuthState in auth-and-billing.mjs — single _setState() guard
// prevents the previous bug class where 4 different code sites independently
// wrote `inProgress = false`.
//
// State semantics:
//   IDLE    — no upload activity; toast is invisible
//   ACTIVE  — at least one upload in flight; spinner + counter visible
//   DONE    — total fully accounted for (completed + failed === total);
//             on_enter arms the 2-second hide timer
//   FADING  — hide timer fired; CSS .fade-out class applied
//   HIDDEN  — fade-out animation complete; element marked hidden=true
//
// Valid transitions:
//   IDLE   → ACTIVE
//   ACTIVE → ACTIVE (overlapping drop adds to running total)
//   ACTIVE → DONE
//   DONE   → ACTIVE  (new upload starts before fade-out timer fires)
//   DONE   → FADING  (2s hide timer fires)
//   FADING → ACTIVE  (new upload starts during fade-out)
//   FADING → HIDDEN  (fade-out animation completes)
//   HIDDEN → ACTIVE  (new upload after toast was fully hidden)
//   HIDDEN → IDLE    (boot-time / catch-all reset)

const UploadToastState = Object.freeze({
    IDLE:   "IDLE",
    ACTIVE: "ACTIVE",
    DONE:   "DONE",
    FADING: "FADING",
    HIDDEN: "HIDDEN",
});

// WHY: @type annotation is intentional — keyed by this.state: string at runtime.
// T|undefined is handled by the !valid guard at callsite.
/** @type {Readonly<Record<string, Set<string>>>} */ // noqa: closed-set-via-satisfies — JSDoc form; intentional string-key table; see WHY comment above
const _UPLOAD_TOAST_TRANSITIONS = Object.freeze({
    [UploadToastState.IDLE]:   new Set([UploadToastState.ACTIVE]),
    [UploadToastState.ACTIVE]: new Set([UploadToastState.ACTIVE, UploadToastState.DONE]),
    [UploadToastState.DONE]:   new Set([UploadToastState.ACTIVE, UploadToastState.FADING]),
    [UploadToastState.FADING]: new Set([UploadToastState.ACTIVE, UploadToastState.HIDDEN]),
    [UploadToastState.HIDDEN]: new Set([UploadToastState.ACTIVE, UploadToastState.IDLE]),
});

// ─── UploadToast class (CLAUDE.md rule #11a: prefer class when state exists) ─
//
// Encapsulates the 5-field mutable struct + 5-state SM that previously lived
// as module-private `_uploadToast` object + free functions.
// The module-level `_toast` singleton preserves the prior singleton semantics;
// the public exports remain thin delegators so all callsites are unchanged.

class UploadToast {
    constructor() {
        /** @type {"IDLE"|"ACTIVE"|"DONE"|"FADING"|"HIDDEN"} */
        this.state     = UploadToastState.IDLE;
        this.total     = 0;
        this.completed = 0;
        this.failed    = 0;
        /** @type {ReturnType<typeof setTimeout> | null} */
        this.hideTimer = null;
    }

    // Single guard for every state transition. Logs invalid transitions instead
    // of throwing — the toast is decorative, and a stuck state should not break
    // upload behaviour. on_enter hooks fire for DONE (arm hide timer) and HIDDEN
    // (clear DOM visibility).
    /** @param {"IDLE"|"ACTIVE"|"DONE"|"FADING"|"HIDDEN"} target @returns {boolean} */
    _setState(target) {
        const valid = _UPLOAD_TOAST_TRANSITIONS[this.state];
        if (!valid || !valid.has(target)) {
            window.adatool?.log?.warn(`[upload-toast] illegal transition ${this.state} -> ${target}; ignoring`);
            return false;
        }
        const prev = this.state;
        this.state = target;
        if (prev !== target) {
            // on_enter hooks. Kept here (not in a hooks map) so the wiring is
            // local + greppable; with only 2 hooks a registry is overkill.
            if (target === UploadToastState.DONE) {
                // Arm the 2-second visible-final-state timer. Cleared if a new
                // upload arrives before it fires (see begin).
                if (this.hideTimer) clearTimeout(this.hideTimer);
                this.hideTimer = setTimeout(() => {
                    this._setState(UploadToastState.FADING);
                }, 2000);
            } else if (target === UploadToastState.FADING) {
                const el = typeof document !== 'undefined'
                    ? document.getElementById('upload-progress-toast')
                    : null;
                if (el) {
                    el.classList.add('fade-out');
                    setTimeout(() => {
                        this._setState(UploadToastState.HIDDEN);
                    }, 400);
                } else {
                    // No element to fade — go straight to HIDDEN.
                    this._setState(UploadToastState.HIDDEN);
                }
            } else if (target === UploadToastState.HIDDEN) {
                const el = typeof document !== 'undefined'
                    ? document.getElementById('upload-progress-toast')
                    : null;
                if (el) {
                    el.hidden = true;
                    el.classList.remove('fade-out');
                }
            }
        }
        return true;
    }

    /** @param {number} count */
    begin(count) {
        // Cancel any pending hide-timer — we are still active.
        if (this.hideTimer) { clearTimeout(this.hideTimer); this.hideTimer = null; }
        const wasActive = this.state === UploadToastState.ACTIVE;
        if (wasActive) {
            // Overlapping drop: add to running total. Don't reset completed/failed —
            // they keep climbing from the first batch.
            this.total += count;
            this._setState(UploadToastState.ACTIVE); // self-loop transition
        } else {
            // Fresh start (from IDLE / DONE / FADING / HIDDEN).
            this.total     = count;
            this.completed = 0;
            this.failed    = 0;
            this._setState(UploadToastState.ACTIVE);
        }
        this.render();
    }

    complete() {
        if (this.completed + this.failed >= this.total) {
            window.adatool?.log?.warn('toast over-count: completed called but already at total');
            return;
        }
        this.completed++;
        this.render();
        this.checkDone();
        // Optimistic quota update: immediately reflect the upload in the banner.
        // _updateQuotaBadge is a no-op stub in app.js until auth-and-billing.mjs
        // is extracted in Phase 9. Reached via window bridge to keep this module
        // import-free (split plan §3 Module 5: "Imports from other split modules: none").
        if (typeof window !== 'undefined' && typeof window._adaUpdateQuotaBadge === 'function') {
            window._adaUpdateQuotaBadge(1);
        }
    }

    fail() {
        if (this.completed + this.failed >= this.total) {
            window.adatool?.log?.warn('toast over-count: fail called but already at total');
            return;
        }
        this.failed++;
        this.render();
        this.checkDone();
    }

    // Called when a `break` aborts the remaining files (quota exceeded or rate limit).
    // Marks the remaining (unattempted) files as failed so the toast resolves.
    /** @param {number} count */
    abortRemaining(count) {
        this.failed += count;
        this.render();
        this.checkDone();
    }

    // Called by a "no-accepted-files-after-zip-extract" early return to dismiss
    // the "Processing..." toast immediately. State machine equivalent of the
    // previous direct `_uploadToast.inProgress = false; _uploadToastCheckDone()`
    // pair. Kept as a named entry point so callers don't reach into _setState.
    dismiss() {
        if (this.state === UploadToastState.ACTIVE) {
            // Force the counters to "complete" so render() shows the final
            // state, then transition to DONE (which arms the fade timer).
            this.total = this.total || 0;
            this.render();
            this._setState(UploadToastState.DONE);
        }
    }

    checkDone() {
        if (this.state !== UploadToastState.ACTIVE) return;
        if (this.completed + this.failed >= this.total) {
            this._setState(UploadToastState.DONE);
        }
    }

    render() {
        if (typeof document === 'undefined') return;
        const el      = document.getElementById('upload-progress-toast');
        const textEl  = document.getElementById('upload-progress-text');
        const fillEl  = document.getElementById('upload-progress-fill');
        if (!el || !textEl || !fillEl) return;

        el.hidden = false;
        el.classList.remove('fade-out');

        const done = this.completed + this.failed;
        const pct  = this.total > 0 ? Math.round((done / this.total) * 100) : 0;
        fillEl.style.width = pct + '%';

        if (this.state !== UploadToastState.ACTIVE) {
            // Final state (DONE / FADING / HIDDEN / IDLE — render snapshots
            // the last counter values until the element fully hides).
            if (this.failed > 0) {
                textEl.textContent = `${this.completed} uploaded, ${this.failed} failed`;
            } else {
                textEl.textContent = `${this.completed} file${this.completed !== 1 ? 's' : ''} uploaded`;
            }
        } else {
            // ACTIVE
            if (this.total === 0) {
                // Still extracting zip/folder — leaf count not yet known.
                textEl.textContent = 'Processing…';
            } else if (this.total === 1) {
                textEl.textContent = 'Uploading 1 file…';
            } else {
                // Clamp to total: when the last file finishes, complete()
                // increments completed (so done === total) and calls render()
                // before checkDone() transitions ACTIVE -> DONE. Without
                // the clamp, the final render shows "Uploading file N+1 of N".
                const current = Math.min(done + 1, this.total);
                textEl.textContent = `Uploading file ${current} of ${this.total}…`;
            }
        }
    }
}

// ─── Module-level singleton ───────────────────────────────────────────────────
const _toast = new UploadToast();

// ─── Public exports (thin delegators — callsites unchanged) ──────────────────

/** @param {number} count */
export function uploadToastBegin(count)          { _toast.begin(count); }
export function uploadToastComplete()            { _toast.complete(); }
export function uploadToastFail()                { _toast.fail(); }
/** @param {number} count */
export function uploadToastAbortRemaining(count) { _toast.abortRemaining(count); }
export function uploadToastDismiss()             { _toast.dismiss(); }
export function uploadToastCheckDone()           { _toast.checkDone(); }
export function uploadToastRender()              { _toast.render(); }
